/**
 * More Content Indicator
 * 
 * This module enhances the engagement tips display by adding
 * "more content available" indicators after each category
 * when displaying partial reports.
 * 
 * It works with the existing HTML output from the engagement-tips-formatter,
 * which renders categories and bullet points in a consistent format.
 */

interface AccessMetadata {
  isFullAccess: boolean;
  hasAccessKey: boolean;
}

class MoreContentIndicator {
  private isFullAccess: boolean = true;
  private initialized: boolean = false;
  private indicatorsAdded: boolean = false;
  
  /**
   * Initialize the indicator manager with access metadata
   * @param accessMetadata Access level information
   */
  initialize(accessMetadata?: { access?: AccessMetadata }): void {
    // Default to full access if no metadata provided
    if (!accessMetadata || !accessMetadata.access) {
      this.isFullAccess = true;
      return;
    }
    
    this.isFullAccess = accessMetadata.access.isFullAccess;
    this.initialized = true;
    
    // If we're in partial access mode, set up the event listener
    if (!this.isFullAccess) {
      this.setupEventListeners();
    }
  }
  
  /**
   * Set up event listeners to add indicators when content is loaded
   */
  private setupEventListeners(): void {
    // Listen for the tab change event to catch when tips are displayed
    document.addEventListener('tabChanged', (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.tabId === 'tips') {
        // Delay slightly to ensure content is fully rendered
        setTimeout(() => this.addMoreContentIndicators(), 100);
      }
    });
    
    // Also add indicators on initial page load if tips are visible
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.addMoreContentIndicators());
    } else {
      this.addMoreContentIndicators();
    }
  }
  
  /**
   * Add "more content" indicators after each category in the engagement tips
   */
  addMoreContentIndicators(): void {
    // Only add indicators if we're in partial access mode and haven't already added them
    if (this.isFullAccess || this.indicatorsAdded) return;
    
    const engagementContent = document.getElementById('engagementContent');
    if (!engagementContent) return;
    
    // Find all category headers
    const categories = engagementContent.querySelectorAll('.category');
    
    categories.forEach(category => {
      // Find the next element (should be a <ul> with the tips)
      const nextElement = category.nextElementSibling;
      if (nextElement && nextElement.tagName === 'UL') {
        // Check if an indicator already exists (avoid duplicates)
        const existingIndicator = nextElement.nextElementSibling;
        if (existingIndicator && existingIndicator.className === 'more-content-container') {
          return;
        }
        
        // Create a container for the "more content" indicator
        const container = document.createElement('div');
        container.className = 'more-content-container';
        
        // Create the indicator element
        const indicator = document.createElement('div');
        indicator.className = 'more-tips-indicator';
        indicator.textContent = 'See all expert recommendations in the full report';
        indicator.onclick = () => this.handleMoreContentClick();
        
        // Add to the container and insert after the tips list
        container.appendChild(indicator);
        nextElement.parentNode?.insertBefore(container, nextElement.nextSibling);
      }
    });
    
    this.indicatorsAdded = true;
  }
  
  /**
   * Handle click on the "more content" indicator
   */
  private handleMoreContentClick(): void {
    // Always use the direct URL approach for consistency with other changes
    window.open("https://blustream.io/contact-sales?utm_source=pxai_journey&utm_medium=internal&utm_campaign=13746024-PXAI%20Journey%20Builder", '_blank');
  }
  
  /**
   * Reset the indicator state (for testing)
   */
  reset(): void {
    this.isFullAccess = true;
    this.initialized = false;
    this.indicatorsAdded = false;
    
    // Remove any existing indicators
    const indicators = document.querySelectorAll('.more-content-container');
    indicators.forEach(indicator => {
      indicator.parentNode?.removeChild(indicator);
    });
  }
}

// Create global instance
(window as any).moreContentIndicator = new MoreContentIndicator();